import { callPlugin } from '../api/client';

/**
 * Web Push registration, rebuilt for this front end.
 *
 * The SERVER half of keystone-push is unchanged and the service worker and
 * manifest are untouched -- only the browser half moves here, because the
 * original was bound to `rl.settings` and `rl-view-model`, neither of which
 * exists outside SnappyMail's own client. See the vault page for the design.
 */

/** applicationServerKey wants raw bytes; Firefox and Safari reject the string form. */
function decodeKey(base64: string): Uint8Array<ArrayBuffer> {
	const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
		.replace(/-/g, '+')
		.replace(/_/g, '/');
	const raw = atob(padded);
	// Backed by a plain ArrayBuffer explicitly: applicationServerKey rejects a
	// view over a SharedArrayBuffer, which is what the bare constructor's type
	// allows for.
	const bytes = new Uint8Array(new ArrayBuffer(raw.length));
	for (let i = 0; i < raw.length; ++i) {
		bytes[i] = raw.charCodeAt(i);
	}
	return bytes;
}

function encodeKey(buffer: ArrayBuffer): string {
	const bytes = new Uint8Array(buffer);
	let raw = '';
	for (let i = 0; i < bytes.length; ++i) {
		raw += String.fromCharCode(bytes[i]);
	}
	return btoa(raw).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export type PushState = 'unsupported' | 'blocked' | 'off' | 'on';

export function pushSupported(): boolean {
	return Boolean(
		typeof navigator !== 'undefined' &&
			navigator.serviceWorker &&
			'PushManager' in window &&
			'Notification' in window
	);
}

export async function pushState(): Promise<PushState> {
	if (!pushSupported()) {
		return 'unsupported';
	}
	if ('denied' === Notification.permission) {
		return 'blocked';
	}
	const registration = await navigator.serviceWorker.getRegistration('/');
	const subscription = await registration?.pushManager.getSubscription();
	return subscription ? 'on' : 'off';
}

/**
 * Turns notifications on for this device.
 *
 * `requestPermission` must be called from a user gesture, so this is only ever
 * invoked straight from a click handler. On iOS it additionally only works once
 * the site has been added to the Home Screen -- Web Push does not exist in plain
 * mobile Safari.
 */
export async function enablePush(vapidKey: string, workerUrl: string): Promise<PushState> {
	if (!pushSupported() || !vapidKey || !workerUrl) {
		return 'unsupported';
	}

	const permission = await Notification.requestPermission();
	if ('granted' !== permission) {
		return 'denied' === permission ? 'blocked' : 'off';
	}

	// Root scope requires Service-Worker-Allowed on the script itself, which the
	// nginx config sets. Same worker URL SnappyMail registers, so there is one
	// registration rather than two competing ones.
	const registration = await navigator.serviceWorker.register(workerUrl, { scope: '/' });
	await navigator.serviceWorker.ready;

	let subscription = await registration.pushManager.getSubscription();

	// A subscription is bound to the VAPID key it was made with. After a key
	// rotation the old one is silently dead -- the push service rejects sends
	// signed by the new key -- so replace it rather than leaving someone with
	// notifications that never arrive.
	const applied = subscription?.options?.applicationServerKey;
	if (subscription && applied && encodeKey(applied) !== vapidKey) {
		await subscription.unsubscribe();
		subscription = null;
	}

	if (!subscription) {
		subscription = await registration.pushManager.subscribe({
			// Mandatory: a push must always show something, which is why the worker
			// falls back to a generic notification.
			userVisibleOnly: true,
			applicationServerKey: decodeKey(vapidKey)
		});
	}

	const result = await callPlugin<{ ok: boolean; reason?: string }>('KeystonePushSubscribe', {
		subscription: subscription.toJSON()
	});

	// The server answers ok:false rather than erroring when it could not attach
	// the device (watcher down, push not configured). Reporting "on" regardless
	// is how people ended up with a switch that said On and no notifications.
	// Release the browser half too, so the next attempt starts clean.
	if (!result?.ok) {
		await subscription.unsubscribe().catch(() => false);
		throw new Error('The server could not register this device for notifications.');
	}

	return 'on';
}

/**
 * Turns them off.
 *
 * The server is told first. Releasing the subscription locally without doing
 * that leaves the watcher sending to a dead endpoint indefinitely.
 */
export async function disablePush(): Promise<PushState> {
	if (!pushSupported()) {
		return 'unsupported';
	}

	const registration = await navigator.serviceWorker.getRegistration('/');
	const subscription = await registration?.pushManager.getSubscription();
	if (!subscription) {
		return 'off';
	}

	try {
		await callPlugin<{ ok: boolean }>('KeystonePushUnsubscribe', {
			endpoint: subscription.endpoint
		});
	} catch {
		// Still release it locally -- a stale server record is recoverable, a
		// browser stuck subscribed against the user's wishes is not.
	}

	await subscription.unsubscribe();
	return 'off';
}
